import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { ConfirmModalData } from "@src/services/state/layoutReducer";
import { hideConfirmModal, showConfirmModal } from "@src/services/state/layoutReducer";
import type * as T from "@src/types";

type ConfirmOptions = Omit<ConfirmModalData, "id" | "message">;

/** Result from confirmModal */
export interface ConfirmModalResult {
  confirmed: boolean;
  /** Prompt input value when promptConfig was provided */
  promptValue?: string;
}

const useLayoutState = () => useSelector((state: T.ReduxState) => state.layout);

// Module-level ref shared across all hook instances
let globalResolveRef: ((value: ConfirmModalResult) => void) | null = null;

/**
 * Hook for showing a non-blocking confirmation modal.
 *
 * Unlike window.confirm(), this modal doesn't block the main thread,
 * allowing polling, fetch requests, and other async operations to continue.
 *
 * @example
 * const { confirmModal } = useConfirmModal();
 *
 * const handleDelete = async () => {
 *   const { confirmed } = await confirmModal("Are you sure you want to delete this item?");
 *   if (!confirmed) return;
 *   // ... proceed with deletion
 * };
 */
export const useConfirmModal = () => {
  const dispatch = useDispatch();
  const { confirmModal: confirmModalData } = useLayoutState();

  /**
   * Show a confirmation modal and wait for user response.
   * For prompts with input, use promptConfig option.
   *
   * @example
   * // Simple confirmation
   * const { confirmed } = await confirmModal("Delete this item?");
   *
   * @example
   * // With prompt input (mimics window.prompt)
   * const { confirmed, promptValue } = await confirmModal("Enter amount", {
   *   promptConfig: { type: "number", placeholder: "0.00" }
   * });
   */
  const confirmModal = useCallback(
    (message: string, options?: ConfirmOptions): Promise<ConfirmModalResult> => {
      return new Promise((resolve) => {
        if (globalResolveRef) {
          globalResolveRef({ confirmed: false });
        }
        globalResolveRef = resolve;

        dispatch(
          showConfirmModal({
            message,
            title: options?.title,
            confirmLabel: options?.confirmLabel,
            cancelLabel: options?.cancelLabel,
            confirmColor: options?.confirmColor,
            promptConfig: options?.promptConfig,
          }),
        );
      });
    },
    [dispatch],
  );

  /** Resolve the current confirmation (called by ConfirmModal component) */
  const resolveConfirm = useCallback((confirmed: boolean, promptValue?: string) => {
    if (globalResolveRef) {
      globalResolveRef({ confirmed, promptValue });
      globalResolveRef = null;
    }
  }, []);

  /**
   * Hide the confirmation modal (called by ConfirmModal component)
   */
  const hideModal = useCallback(() => {
    dispatch(hideConfirmModal());
  }, [dispatch]);

  return {
    confirmModal,
    confirmModalData,
    resolveConfirm,
    hideModal,
  };
};
